<script setup>
/**
 * A célula de ORIGEM do contato — por qual dos números da conta ele chegou.
 *
 * Fica ALINHADA À DIREITA do card, com largura fixa, e é isso que a faz ler como COLUNA:
 * os valores caem na mesma vertical em toda a lista, que é o que o olho procura ao varrer
 * "quais vieram da Loja 2". Jogada na linha de metadados (e-mail · telefone · país) ela
 * viraria mais um item de uma frase, e a leitura vertical se perderia.
 *
 * Três estados, e cada um existe por um motivo:
 *   carregando  → traço fantasma, sem esqueleto pulsante. A resposta chega em um lote
 *                 único e rápido; animar 25 linhas por 200ms é ruído, não informação.
 *   sem origem  → NADA. Contato cadastrado à mão não tem origem, e contato cuja caixa
 *                 está fora do alcance de quem lê não pode nem sinalizar que existe —
 *                 um "restrito" já contaria o que a caixa fechada esconde.
 *   com origem  → o nome da caixa, com a data do primeiro contato no title.
 */
import { computed } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useContactOrigin } from 'dashboard/composables/cw_custom/useContactOrigins.js';

const props = defineProps({
  contactId: { type: Number, required: true },
});

const accountId = useMapGetter('getCurrentAccountId');
const { origem, carregando } = useContactOrigin(
  () => props.contactId,
  accountId
);

const descricao = computed(() => {
  if (!origem.value) return '';

  const segundos = origem.value.primeiroContatoEm;
  if (!segundos) return `Primeiro contato por ${origem.value.nome}`;

  const data = new Date(segundos * 1000).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
  return `Primeiro contato por ${origem.value.nome} em ${data}`;
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template -->
  <div class="flex-col items-end hidden gap-0.5 sm:flex shrink-0 w-36">
    <span
      v-if="carregando"
      class="text-mj-15 select-none text-n-slate-8"
      aria-hidden="true"
    >
      —
    </span>
    <span
      v-else-if="origem"
      class="max-w-full text-mj-15 truncate text-n-slate-11"
      :title="descricao"
    >
      {{ origem.nome }}
    </span>
  </div>
</template>
